import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

export const PIE_CARD_COLORS = [
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#6366f1',
  '#e11d48',
  '#a855f7',
  '#14b8a6',
  '#84cc16',
];

export type PieCardDatum = { label: string; value: number };

function formatPieShare(value: number, total: number) {
  if (total > 0 && total <= 110) {
    return `${Number(value).toFixed(1)}%`;
  }
  return Number(value).toLocaleString();
}

interface PieCardWithLegendProps {
  title?: string;
  subtitle?: string;
  data: PieCardDatum[];
}

const PieCardWithLegend: React.FC<PieCardWithLegendProps> = ({ title, subtitle, data }) => {
  const total = data.reduce((sum, row) => sum + row.value, 0);
  const shareColumn = total > 0 && total <= 110 ? 'Share' : 'Count';

  return (
    <div className="card" style={{ padding: 14, marginBottom: 0 }}>
      {subtitle ? (
        <h4 style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12 }}>{subtitle}</h4>
      ) : null}
      {title ? <h3 style={{ marginTop: subtitle ? 0 : undefined }}>{title}</h3> : null}
      {data.length > 0 ? (
        <>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="label"
                >
                  {data.map((_, i) => (
                    <Cell key={`slice-${i}`} fill={PIE_CARD_COLORS[i % PIE_CARD_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, _name: string, item: any) => [
                    formatPieShare(Number(value), total),
                    item?.payload?.label || '',
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: 12,
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ width: 24 }} />
                <th style={{ textAlign: 'left', padding: '6px 4px' }}>Category</th>
                <th style={{ textAlign: 'right', padding: '6px 4px' }}>{shareColumn}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry, index) => (
                <tr key={`${entry.label}-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: PIE_CARD_COLORS[index % PIE_CARD_COLORS.length],
                      }}
                    />
                  </td>
                  <td style={{ padding: '4px' }}>{entry.label}</td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>
                    {formatPieShare(entry.value, total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p style={{ color: '#6b7280', fontSize: 13 }}>Data not available.</p>
      )}
    </div>
  );
};

export function objectToPieData(obj: Record<string, number> | null | undefined): PieCardDatum[] {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([label, value]) => ({ label, value: Number(value) }))
    .filter((row) => row.label && !Number.isNaN(row.value) && row.value > 0);
}

export default PieCardWithLegend;
